import React from 'react';
import PropTypes from 'prop-types';

import RavenUtil from 'APP_ROOT/components/common/ravenUtil';
import { strings } from '../../../localization';

const TIME_THRESHOLD_IN_MS = 15000;
const MINIMUM_DISTANCE_CHANGE_IN_KM = 0.1;
const DISTANCE_THRESHOLD_IN_KM = 0.5;

/**
 * LocationLabel gets a location Geostamp as a prop (Geostamp={lon,lat,ts}) and does
 * a reverse geocode mapbox lookup for a human friendly address.  It displays a blank
 * while the request is in progress and the result of the lookup when it completes.
 *
 * If a new location is given that is different enough (based on distance / time) from
 * the previous one, a new lookup is done.
 * */
export default class LocationLabel extends React.PureComponent {

    state = {
        labelText: '',
    }

    constructor(props) {
        super(props);
        this._latestLookup = null; // remember the latest lookup we did so we can calculate distance and time diff
    }

    componentDidMount() {
        if(this.props.location) {
            this.sendRequest(this.props.location);
        }
    }

    componentDidUpdate(oldProps) {
        if(oldProps.location !== this.props.location) {
            this.handleLocationChange(this.props.location);
        }
    }

    componentWillUnmount() {
        this._ignorePendingRequests = true;
    }

    sendRequest = location => {
        this.setState({
            labelText:'',
        });
        this._latestLookup = location;
        RavenUtil.getLocationFeatures(location.lon, location.lat,
            response => {
                if (this._ignorePendingRequests) return;
                const features = response && response.data && response.data.features;
                if (!features) {
                    this.setState({
                        labelText: 'Address lookup error'
                    });
                    return;
                }
                const labelText = this.props.withStreetNumber ?
                    RavenUtil.getLocationName(response)
                    : RavenUtil.getLocationString(features, true);
                this.setState({ labelText });
            },
            err => {
                console.error('Error in LocationLabel.sendRequest' , err);
            }
        );
    }

    subtractTimestamps = (g1, g2) => {
        const ts1 = RavenUtil.toDate(g1.ts);
        const ts2 = RavenUtil.toDate(g2.ts);
        return ts2 - ts1;
    }

    calculateDistanceInKm = (g1, g2) => {
        return RavenUtil.distance(g1.lat, g1.lon, g2.lat, g2.lon, 'K');
    }

    handleLocationChange = location => {
        if (!location) {
            this.setState({ labelText: '' });
            this._latestLookup = null;
            return;
        }
        const prev = this._latestLookup;
        if (!prev) {
            this.sendRequest(location);
            return;
        }
        const distanceSinceLastLookup = this.calculateDistanceInKm(prev, location);
        if(distanceSinceLastLookup > DISTANCE_THRESHOLD_IN_KM) {
            this.sendRequest(location);
            return;
        }
        const timeDiff = this.subtractTimestamps(prev, location);
        if (timeDiff > TIME_THRESHOLD_IN_MS && distanceSinceLastLookup > MINIMUM_DISTANCE_CHANGE_IN_KM) {
            this.sendRequest(location);
        }
    }

    render() {
        if(this.props.addMapLink)
        {
            if (this.props.location) {
                var url = "https://www.google.com/maps/place/" + this.props.location.lat + "," + this.props.location.lon + "/@" + this.props.location.lat + "," + this.props.location.lon + ",14z";
                return <span className="location-label" title={this.state.labelText}><span>{this.state.labelText}</span><a target="_blank" rel="noopener noreferrer" href={url}><i className="material-icons" title={strings.google_maps}>launch</i></a></span>
            }
        }
        else
        {
            return <span className="location-label" title={this.state.labelText}>{this.state.labelText}</span>
        }
        return null;
    }
}

LocationLabel.propTypes = {
    location: PropTypes.shape({
        lon: PropTypes.number.isRequired,
        lat: PropTypes.number.isRequired,
        ts: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired
    }),
    withStreetNumber: PropTypes.bool.isRequired,
};
